import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components/button";
import { UploadEntryCard } from "@/shared/components/UploadEntryCard";
import type { HealthRecord } from "@/shared/types";
import { RecordsUploadModal } from "@/features/patient/records/components/RecordsUploadModal";
import { RecentUploadsCard } from "@/features/patient/records/components/RecentUploadsCard";
import { RecentDicomUploadsCard } from "@/features/patient/diagnostics/components/imaging/RecentImagingUploadsCard";
import { ImagingUploadModal } from "@/features/patient/diagnostics/components/imaging/ImagingUploadModal";
import { useFamily } from "@/portals/patient/context/FamilyContext";

interface UploadRecordsProps {
  onUpload: (records: HealthRecord[]) => void;
}

/**
 * Upload Records — two entry points (Medical Records, DICOM), each a
 * compact "+ Add" trigger card that opens its own upload modal, per product
 * decision: no permanently-visible upload form on this page any more (the
 * old always-open drop zone + privacy panel are gone). Below the triggers,
 * a 50/50 split shows the 3 most recent uploads of each kind side by side.
 * DICOM upload lives here now instead of on Diagnostics & Imaging (which
 * stays browse/view-only — list + viewer, no trigger of its own).
 *
 * Neither modal is handed an onUploaded/onStudyUploaded-style callback any
 * more: both close the instant their upload is accepted (see
 * RecordsUploadModal/ImagingUploadModal's own docstrings) and progress is
 * tracked by a shared, always-mounted context from then on
 * (UploadHistoryContext / ImagingStudiesContext) — that context refreshes
 * its own list on completion, so this page doesn't need to react to it at
 * all.
 */
export function UploadRecords({ onUpload }: UploadRecordsProps) {
  const navigate = useNavigate();
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;

  const [recordsModalOpen, setRecordsModalOpen] = useState(false);
  const [dicomModalOpen, setDicomModalOpen] = useState(false);

  return (
    <div className="bg-[#F7F7F7] p-4 lg:p-6">
      <div className="grid gap-2.5">
        <UploadEntryCard
          icon="lucide:file-text"
          title="Medical Records"
          description="Upload your medical records in PDF, JPG, PNG and other formats"
          onClick={() => setRecordsModalOpen(true)}
        />
        <UploadEntryCard
          icon="lucide:scan-line"
          title="Xray / CT Scan / MRI"
          description="Click to upload your DICOM images for the viewer"
          onClick={() => setDicomModalOpen(true)}
        />
      </div>

      {/* Every upload's files/issues/parsed data lives on its own page
          (see UploadHistoryView) instead of inline here. */}
      <Button
        onClick={() => navigate("/patient/documents")}
        className="!mt-5 !h-auto !rounded-xl !bg-[#E4002B]/50 !px-5 !py-2.5 !text-sm !font-extrabold !text-white hover:!bg-[#E4002B]/60"
      >
        View Recent Uploads
      </Button>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <RecentUploadsCard heading="Recent Medical Records Uploads" />
        <RecentDicomUploadsCard />
      </div>

      <RecordsUploadModal
        open={recordsModalOpen}
        onClose={() => setRecordsModalOpen(false)}
        onUpload={onUpload}
      />
      <ImagingUploadModal
        open={dicomModalOpen}
        onClose={() => setDicomModalOpen(false)}
        actingAsProfileId={actingAsProfileId}
      />
    </div>
  );
}
